import React from "react";
import type { ChartComponentProps } from "./types";
import { ChartContainer } from "./ChartContainer";
import { useChartAnimation } from "./useChartAnimation";
import {
  FONT,
  formatChartValue,
  estimateTextWidth,
  useAvailableWidth,
  computeYAxis,
} from "./chartUtils";
import { E } from "./chartColors";
import { ChartTitle } from "./ChartTitle";

const FIGMA_MIN_W = 786;
const FALLBACK_W = 960;
const PLOT_INSET_LEFT = 20;
const PLOT_INSET_RIGHT = 30;

export const LineChart: React.FC<ChartComponentProps> = ({
  chartData,
  ctx,
}) => {
  const { S } = ctx;
  const anim = useChartAnimation(ctx, chartData);
  const dataPoints = chartData.dataPoints || [];
  const referenceLine = chartData.referenceLine;
  const [measureRef, availPx] = useAvailableWidth();

  if (dataPoints.length < 2) {
    return null;
  }

  const values = dataPoints.map((d) => d.value);
  const allValues = referenceLine ? [...values, referenceLine.value] : values;
  const { yMin, yMax, ticks: yTickValues } = computeYAxis(allValues);

  const padTop = 56;
  const plotH = 340;

  const formatVal = (v: number) => formatChartValue(v);

  const maxLabelWidth = Math.max(
    ...yTickValues.map((v) => estimateTextWidth(formatVal(v), 20)),
  );
  const padLeft = Math.max(60, Math.ceil(maxLabelWidth) + 24);

  const W_from_container = availPx > 0 ? Math.round(availPx / S) : FALLBACK_W;
  const W = Math.max(FIGMA_MIN_W, W_from_container);
  const plotW = W - padLeft;
  const drawableW = plotW - PLOT_INSET_LEFT - PLOT_INSET_RIGHT;

  const sampleLabel = dataPoints[Math.floor(dataPoints.length / 2)].label;
  const estLabelW = estimateTextWidth(sampleLabel, 20);
  const pointSpacing = drawableW / Math.max(1, dataPoints.length - 1);

  const minHorizontalGap = estLabelW + 24;
  const horizontalInterval = Math.max(
    1,
    Math.ceil(minHorizontalGap / pointSpacing),
  );
  const useRotation = horizontalInterval >= 4 && dataPoints.length > 8;

  let xLabelInterval: number;
  if (dataPoints.length <= 10) {
    xLabelInterval = 1;
  } else if (useRotation) {
    const minRotatedGap = Math.max(48, estLabelW * 0.4);
    xLabelInterval = Math.max(1, Math.ceil(minRotatedGap / pointSpacing));
  } else {
    xLabelInterval = horizontalInterval;
  }

  const xVisibleIndices = new Set<number>();
  xVisibleIndices.add(0);
  xVisibleIndices.add(dataPoints.length - 1);
  for (let i = 0; i < dataPoints.length; i += xLabelInterval) {
    xVisibleIndices.add(i);
  }
  if (xLabelInterval > 1) {
    const lastIdx = dataPoints.length - 1;
    const prevStep = lastIdx - (lastIdx % xLabelInterval || xLabelInterval);
    if (prevStep > 0 && lastIdx - prevStep < xLabelInterval * 0.6) {
      xVisibleIndices.delete(prevStep);
    }
  }

  const rotatedExtraH = useRotation
    ? Math.min(Math.ceil(estLabelW * 0.65), 80) + 8
    : 0;
  const H = padTop + plotH + (useRotation ? 20 + rotatedExtraH : 72);

  const allInflections: number[] = [];
  for (let i = 1; i < dataPoints.length - 1; i++) {
    const prev = dataPoints[i - 1].value;
    const curr = dataPoints[i].value;
    const next = dataPoints[i + 1].value;
    if ((curr > prev && curr > next) || (curr < prev && curr < next)) {
      allInflections.push(i);
    }
  }

  const maxValueLabels = Math.max(4, Math.ceil(dataPoints.length / 4));
  let valueLabelIndices: Set<number>;

  if (allInflections.length + 2 <= maxValueLabels) {
    valueLabelIndices = new Set([0, dataPoints.length - 1, ...allInflections]);
  } else {
    valueLabelIndices = new Set<number>();
    valueLabelIndices.add(0);
    valueLabelIndices.add(dataPoints.length - 1);

    const windowCount = Math.max(2, Math.floor(maxValueLabels / 2));
    const windowSize = Math.ceil(dataPoints.length / windowCount);
    for (let w = 0; w < windowCount; w++) {
      const start = w * windowSize;
      const end = Math.min(start + windowSize, dataPoints.length);
      if (start >= dataPoints.length) {
        break;
      }
      let maxIdx = start;
      let minIdx = start;
      for (let i = start + 1; i < end; i++) {
        if (dataPoints[i].value > dataPoints[maxIdx].value) {
          maxIdx = i;
        }
        if (dataPoints[i].value < dataPoints[minIdx].value) {
          minIdx = i;
        }
      }
      valueLabelIndices.add(maxIdx);
      if (maxIdx !== minIdx) {
        valueLabelIndices.add(minIdx);
      }
    }
  }

  const getX = (i: number) =>
    padLeft + PLOT_INSET_LEFT + (i / (dataPoints.length - 1)) * drawableW;
  const getY = (v: number) => padTop + (1 - (v - yMin) / (yMax - yMin)) * plotH;

  const points = dataPoints.map((d, i) => ({
    x: getX(i),
    y: getY(d.value),
  }));
  const animatedPoints = points.map((p) => ({
    x: p.x,
    y: padTop + plotH + (p.y - padTop - plotH) * anim.progress,
  }));

  const pathD = animatedPoints
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  const refY = referenceLine ? getY(referenceLine.value) : null;

  return (
    <ChartContainer ctx={ctx} chartData={chartData}>
      <div ref={measureRef} style={{ width: "100%" }}>
        {chartData.label && (
          <ChartTitle label={chartData.label} unit={chartData.unit} S={S} />
        )}

        <svg viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
          {yTickValues.map((val, i) => {
            const y = getY(val);
            return (
              <g key={i}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={W}
                  y2={y}
                  stroke={E.chartGrid}
                  strokeWidth={1}
                />
                <text
                  x={padLeft - 8}
                  y={y + 7}
                  textAnchor="end"
                  fill={E.muted}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={400}
                >
                  {formatVal(val)}
                </text>
              </g>
            );
          })}

          {referenceLine && refY !== null && (
            <g opacity={anim.progress}>
              <line
                x1={padLeft}
                y1={refY}
                x2={W}
                y2={refY}
                stroke={E.orange}
                strokeWidth={2}
                strokeDasharray="8 6"
              />
              <text
                x={W + 8}
                y={refY + 7}
                fill={E.orange}
                fontSize={20}
                fontFamily={FONT}
                fontWeight={400}
              >
                {referenceLine.label}
              </text>
            </g>
          )}

          <path
            d={pathD}
            fill="none"
            stroke={E.cyan}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {animatedPoints.map((p, i) => {
            const stagger = anim.stagger(i, 0.12);
            const hasLabel = valueLabelIndices.has(i);
            if (!hasLabel) {
              return null;
            }
            const isBelow =
              i > 0 && animatedPoints[i].y > animatedPoints[i - 1].y;
            return (
              <g key={i} opacity={stagger.opacity}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={5}
                  fill="var(--edgen-background_solid_secondary)"
                  stroke={E.cyan}
                  strokeWidth={2}
                />
                <text
                  x={p.x}
                  y={isBelow ? p.y + 28 : p.y - 12}
                  textAnchor="middle"
                  fill={E.t3}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={500}
                >
                  {formatVal(dataPoints[i].value)}
                </text>
              </g>
            );
          })}

          {dataPoints.map((d, i) => {
            if (!xVisibleIndices.has(i)) {
              return null;
            }
            const x = getX(i);
            if (useRotation) {
              return (
                <g key={i}>
                  <line
                    x1={x}
                    y1={padTop + plotH}
                    x2={x}
                    y2={padTop + plotH + 6}
                    stroke={E.muted}
                    strokeWidth={1}
                  />
                  <text
                    x={x - 4}
                    y={padTop + plotH + 24}
                    textAnchor="end"
                    fill={E.muted}
                    fontSize={20}
                    fontFamily={FONT}
                    fontWeight={400}
                    transform={`rotate(-45, ${x - 4}, ${padTop + plotH + 24})`}
                  >
                    {d.label}
                  </text>
                </g>
              );
            }
            const shownSpacing = xLabelInterval * pointSpacing;
            const labelW = Math.min(shownSpacing, 140);
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={padTop + plotH}
                  x2={x}
                  y2={padTop + plotH + 6}
                  stroke={E.muted}
                  strokeWidth={1}
                />
                <foreignObject
                  x={x - labelW / 2}
                  y={padTop + plotH + 10}
                  width={labelW}
                  height={56}
                >
                  <div
                    style={{
                      width: "100%",
                      textAlign: "center",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                      fontFamily: FONT,
                      fontSize: 20,
                      fontWeight: 400,
                      color: E.muted,
                      lineHeight: "24px",
                    }}
                  >
                    {d.label}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </div>
    </ChartContainer>
  );
};
